import type { SafeTOrgKey } from './types';

export interface ClientBenchmarks {
  engagedGoal?: number;
  acquiredGoal?: number;
  intlTestingAcquiredGoal?: number;
}

/** Long-term annual goals for Skyway chart target lines. */
export const SKYWAY_LONG_TERM_GOALS = { engagedPerYear: 10, acquiredPerYear: 5 } as const;

/** Long-term annual goals for DronePort chart target lines. */
export const DRONEPORT_LONG_TERM_GOALS = { engagedPerYear: 4, acquiredPerYear: 2 } as const;

/** Long-term annual goals for combined (Skyway + DronePort) chart target lines. */
export const COMBINED_LONG_TERM_GOALS = {
  engagedPerYear: SKYWAY_LONG_TERM_GOALS.engagedPerYear + DRONEPORT_LONG_TERM_GOALS.engagedPerYear,
  acquiredPerYear: SKYWAY_LONG_TERM_GOALS.acquiredPerYear + DRONEPORT_LONG_TERM_GOALS.acquiredPerYear,
} as const;

export interface RevenueBenchmarks {
  /** Annual revenue goal (USD) */
  annualRevenueGoal?: number;
}

export function parseQuarterLabel(label: string): { year: number; quarter: 1 | 2 | 3 | 4 } | null {
  // expected: "Q1 2025"
  const m = label.match(/^Q([1-4])\s+(\d{4})$/);
  if (!m) return null;
  return { year: Number(m[2]), quarter: Number(m[1]) as 1 | 2 | 3 | 4 };
}

/**
 * Client acquisition benchmarks derived from narrative requirements + subawardee objectives.
 * Displayed as per-quarter pace lines on the quarterly chart.
 */
export function getClientBenchmarksForQuarterLabel(
  quarterLabel: string,
  scope: { mode: 'org'; orgKey: SafeTOrgKey } | { mode: 'combined' }
): ClientBenchmarks {
  const parsed = parseQuarterLabel(quarterLabel);
  if (!parsed) return {};
  const { year } = parsed;

  const orgBench = (orgKey: SafeTOrgKey): ClientBenchmarks => {
    if (orgKey === 'skyway') {
      // engaged: 4 in 2025, 10/year 2026-2029
      // acquired: 5/year starting Jan 2027
      const engagedAnnual = year === 2025 ? 4 : year >= 2026 && year <= 2029 ? 10 : undefined;
      const acquiredAnnual = year >= 2027 && year <= 2029 ? 5 : 0;
      return {
        engagedGoal: engagedAnnual !== undefined ? engagedAnnual / 4 : undefined,
        acquiredGoal: acquiredAnnual !== undefined ? acquiredAnnual / 4 : undefined,
      };
    }

    // DronePort Network
    // From requirement text + DronePort objectives:
    // - pilot clients acquired: 2 in 2027 (objective: "two pilot clients" by 2027-12-31)
    // - international testing customers acquired: 2 by end of 2029
    // - potential clients engaged: 4 by end of 2027 (we render as a 2027 pace line)
    const engagedAnnual = year === 2027 ? 4 : 0;
    const acquiredAnnual = year === 2027 ? 2 : 0;
    const intlAnnual = year === 2029 ? 2 : 0;
    return {
      engagedGoal: engagedAnnual / 4,
      acquiredGoal: acquiredAnnual / 4,
      intlTestingAcquiredGoal: intlAnnual / 4,
    };
  };

  if (scope.mode === 'combined') {
    const sky = orgBench('skyway');
    const dp = orgBench('droneport');
    return {
      engagedGoal: (sky.engagedGoal ?? 0) + (dp.engagedGoal ?? 0),
      acquiredGoal: (sky.acquiredGoal ?? 0) + (dp.acquiredGoal ?? 0),
      intlTestingAcquiredGoal:
        (sky.intlTestingAcquiredGoal ?? 0) + (dp.intlTestingAcquiredGoal ?? 0),
    };
  }

  return orgBench(scope.orgKey);
}

/**
 * Returns Skyway annual goals for a given year (engaged and acquired).
 * engaged: 4 in 2025, 10/year 2026-2029; acquired: 5/year 2027-2029
 */
export function getSkywayAnnualGoalsForYear(year: number): { engaged: number | undefined; acquired: number | undefined } {
  const engagedAnnual = year === 2025 ? 4 : year >= 2026 && year <= 2029 ? 10 : undefined;
  const acquiredAnnual = year >= 2027 && year <= 2029 ? 5 : undefined;
  return { engaged: engagedAnnual, acquired: acquiredAnnual };
}

/**
 * Returns DronePort annual goals for a given year (engaged and acquired).
 * From DronePort objectives: 4 potential clients engaged by end of 2027;
 * 2 pilot clients acquired in 2027. International testing (2 by 2029) tracked separately.
 */
export function getDronePortAnnualGoalsForYear(year: number): { engaged: number | undefined; acquired: number | undefined } {
  const engagedAnnual = year === 2027 ? 4 : undefined;
  const acquiredAnnual = year === 2027 ? 2 : undefined;
  return { engaged: engagedAnnual, acquired: acquiredAnnual };
}

/**
 * Returns combined (Skyway + DronePort) annual goals for a given year.
 */
export function getCombinedAnnualGoalsForYear(year: number): { engaged: number | undefined; acquired: number | undefined } {
  const sky = getSkywayAnnualGoalsForYear(year);
  const dp = getDronePortAnnualGoalsForYear(year);
  const engaged = sky.engaged !== undefined || dp.engaged !== undefined
    ? (sky.engaged ?? 0) + (dp.engaged ?? 0)
    : undefined;
  const acquired = sky.acquired !== undefined || dp.acquired !== undefined
    ? (sky.acquired ?? 0) + (dp.acquired ?? 0)
    : undefined;
  return { engaged, acquired };
}

/**
 * Revenue benchmark: $500k/year annual goal (shown as per-quarter pace).
 */
export function getRevenueBenchmarks(scope: { mode: 'org'; orgKey: SafeTOrgKey } | { mode: 'combined' }): RevenueBenchmarks {
  // All modes use $500k annual goal
  return { annualRevenueGoal: 500_000 };
}
